import dayjs from "dayjs";
import type { SingleDateFormFields } from "../shared/SingleDateForm";
import type {
  BodyMapComponentSpec,
  BodyMapQuestionKey,
  MetricOption,
  SubjectEntity,
} from "../../Processing/types";

export interface BodyMapFormState extends SingleDateFormFields {
  /** UUID of the Slicer governing the athlete, when bound. */
  singlePlayerControllerId?: string;
  title: string;
  question_key: BodyMapQuestionKey | "";
  queryId: string;
  playerSubject: string; // "player-{id}", "" when unset
  expandedKey: string | string[]; // UI-only: controlled accordion step, not persisted
  // UI-only: once the user edits the title we stop auto-following the player name.
  titleTouched: boolean;
}

export type BodyMapAction =
  | { type: "SET_TITLE"; title: string }
  | { type: "SET_QUESTION_KEY"; value: BodyMapQuestionKey }
  // defaultTitle (the selected player's name) is applied as the title only while
  // the user hasn't manually edited it.
  | { type: "SET_PLAYER"; playerSubject: string; defaultTitle?: string }
  | { type: "UPDATE_SINGLE_DATE"; patch: Partial<SingleDateFormFields> }
  | { type: "SET_PLAYER_CONTROLLER"; controllerId?: string }
  | { type: "SET_EXPANDED_KEY"; key: string | string[] };

export function bodyMapReducer(state: BodyMapFormState, action: BodyMapAction): BodyMapFormState {
  switch (action.type) {
    case "SET_TITLE":
      return { ...state, title: action.title, titleTouched: true };
    case "SET_QUESTION_KEY":
      return { ...state, question_key: action.value };
    case "SET_PLAYER":
      return {
        ...state,
        playerSubject: action.playerSubject,
        title: state.titleTouched ? state.title : action.defaultTitle ?? state.title,
      };
    case "UPDATE_SINGLE_DATE":
      return { ...state, ...action.patch };
    case "SET_PLAYER_CONTROLLER":
      return { ...state, singlePlayerControllerId: action.controllerId };
    case "SET_EXPANDED_KEY":
      return { ...state, expandedKey: action.key };
    default:
      return state;
  }
}

export function createInitialState(): BodyMapFormState {
  return {
    title: "",
    question_key: "",
    queryId: crypto.randomUUID(),
    playerSubject: "",
    dateMode: "relative",
    dateOffset: 0,
    fixedDate: dayjs().format("YYYY-MM-DD"),
    expandedKey: ["__type"],
    titleTouched: false,
  };
}

export function hydrateFromSpec(spec: BodyMapComponentSpec): BodyMapFormState {
  const misc = spec.body_map_builder_misc ?? {};
  // Legacy specs used dateMode "latest"/"fixed"; map them onto the new
  // relative/absolute model so older widgets keep editing cleanly.
  const legacyMode = misc.dateMode as string | undefined;
  const isAbsolute = legacyMode === "absolute" || legacyMode === "fixed";
  return {
    title: spec.display.title ?? "",
    question_key: spec.question_key,
    queryId: spec.queryId,
    playerSubject: misc.playerSubject ?? "",
    dateMode: isAbsolute ? "absolute" : "relative",
    dateOffset: misc.dateOffset ?? 0,
    fixedDate: misc.fixedDate ?? "",
    singleDateControllerId: misc.singleDateControllerId,
    singlePlayerControllerId: misc.singlePlayerControllerId,
    expandedKey: [],
    // A saved widget already has its intended title; don't let player changes overwrite it.
    titleTouched: true,
  };
}

export function formStateToSpec(
  state: BodyMapFormState,
  _metrics: MetricOption[],
  _getSubjectEntities?: (t: string) => SubjectEntity[],
): BodyMapComponentSpec | null {
  // Invalid until a body map question exists, a single athlete is chosen, and
  // (in absolute mode) a date is set.
  if (!state.question_key) return null;
  if (!state.playerSubject) return null;
  if (state.dateMode === "absolute" && !state.fixedDate) return null;

  return {
    type: "body_map",
    display: { title: state.title || "Body Map" },
    question_key: state.question_key,
    queryId: state.queryId,
    body_map_builder_misc: {
      playerSubject: state.playerSubject,
      dateMode: state.dateMode,
      dateOffset: state.dateOffset,
      fixedDate: state.fixedDate,
      singleDateControllerId: state.singleDateControllerId,
      singlePlayerControllerId: state.singlePlayerControllerId,
    },
  };
}
